import * as React from "react";
import { useQuery, useQueryClient, type Query } from "@tanstack/react-query";

import {
  AWAY_POLLS_BEFORE_OUTAGE,
  awayPollStreak,
  isBackendUnavailable,
  onBackendUnavailable,
} from "@/api/connectivity";
import { appVersionQuery, keys, type AppVersion } from "@/api/hooks";
import { offersReloadTo, reloadedForSha, tabBuild, type TabBuild } from "@/api/loadedBuild";
import type { PublicMaintenance } from "@/api/types";

/** The SPA's view of the server between two requests: the public
 *  maintenance state from ``GET /api/version`` and whether the backend
 *  can be reached at all.
 *
 *  2026-09-24: the maintenance banner read only ``/auth/me``, which is
 *  never re-polled, so a deploy's "maintenance on" never reached an open
 *  tab; when the backend then restarted, requests just failed. Polling the
 *  unauthenticated version endpoint shows the banner ahead of the restart,
 *  keeps its last message up through it, and notices the recovery. */

export const SERVER_STATUS_POLL_MS = 20_000;
/** While the backend is away or a reconnect is in progress. */
export const SERVER_STATUS_FAST_POLL_MS = 5_000;
/** The refresh after an outage waits a random 0–3 s: every open tab sees
 *  the backend return on the same poll tick, and a backend that has just
 *  started should not get every tab's every query in the same instant. */
export const RECOVERY_REFRESH_SPREAD_MS = 3_000;

export function serverPollInterval(
  query: Pick<Query, "state">,
  reconnecting: boolean,
): number {
  return reconnecting || query.state.status === "error"
    ? SERVER_STATUS_FAST_POLL_MS
    : SERVER_STATUS_POLL_MS;
}

export type ServerStatus = {
  /** Last known state; ``null`` until known or on a backend that
   *  predates it (use ``/auth/me``'s then). Kept while unreachable. */
  maintenance: PublicMaintenance | null;
  /** The last polls (``AWAY_POLLS_BEFORE_OUTAGE`` in a row) failed the
   *  way a restarting backend fails. */
  unreachable: boolean;
  /** The server runs another build than this tab (a deploy, or the
   *  rollback of one): offer a reload. The tab's own build is the one
   *  baked into the bundle (``tabBuild``); see ``offersReloadTo``. */
  updateAvailable: boolean;
};

export function serverStatusFrom(
  version: {
    data?: AppVersion | null;
    isError: boolean;
    error: unknown;
  },
  tab: TabBuild | null = tabBuild(),
  reloadedFor: string | null = reloadedForSha(),
  awayPolls: number = awayPollStreak(),
): ServerStatus {
  const server = version.data;
  // While a deploy holds strict maintenance the new frontend can be up
  // before the new backend (migrate still running), so a tab on the new
  // bundle sees an older server build. Offer the reload once maintenance is
  // released — deploy.sh releases it after a rollback too.
  const deploying = Boolean(server?.maintenance?.enabled && server.maintenance.strict);
  return {
    maintenance: server?.maintenance ?? null,
    unreachable:
      version.isError &&
      isBackendUnavailable(version.error) &&
      awayPolls >= AWAY_POLLS_BEFORE_OUTAGE,
    updateAvailable: Boolean(server && !deploying && offersReloadTo(server, tab, reloadedFor)),
  };
}

/** Poll ``/api/version``. Mount ONCE (``RequireAuth`` does); everything
 *  else reads the same query through ``useAppVersion()``.
 *
 *  * every 20 s, every 5 s while the last poll failed or ``reconnecting``,
 *    and at once when the tab comes back into view;
 *  * at once when another request finds the backend gone;
 *  * a changed maintenance state refreshes ``/auth/me`` (who set it);
 *  * another build than the one this tab runs sets ``updateAvailable``;
 *  * the first answer after an outage (``AWAY_POLLS_BEFORE_OUTAGE`` failed
 *    polls in a row) refreshes every query on screen, within
 *    ``RECOVERY_REFRESH_SPREAD_MS`` — they failed or went stale while the
 *    backend was down. */
export function useServerStatusPoller({
  reconnecting = false,
}: { reconnecting?: boolean } = {}): ServerStatus {
  const qc = useQueryClient();
  const version = useQuery({
    ...appVersionQuery,
    refetchInterval: (query) => serverPollInterval(query, reconnecting),
    // A hidden tab does not poll (``refetchIntervalInBackground`` is off);
    // back in view it asks at once rather than show a stale state.
    refetchOnWindowFocus: "always",
  });
  const status = serverStatusFrom(version);

  const wasUnreachable = React.useRef(false);
  React.useEffect(() => {
    if (status.unreachable) {
      wasUnreachable.current = true;
      return;
    }
    if (!version.isSuccess || !wasUnreachable.current) return;
    const id = window.setTimeout(() => {
      // Cleared first (unmount, another outage)? Then it is still owed.
      wasUnreachable.current = false;
      void qc.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== keys.version[0],
      });
    }, Math.random() * RECOVERY_REFRESH_SPREAD_MS);
    return () => window.clearTimeout(id);
  }, [status.unreachable, version.isSuccess, qc]);

  const maintenanceKey = version.isSuccess ? JSON.stringify(status.maintenance) : null;
  const lastMaintenanceKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (maintenanceKey === null) return;
    const previous = lastMaintenanceKey.current;
    lastMaintenanceKey.current = maintenanceKey;
    if (previous !== null && previous !== maintenanceKey) {
      void qc.invalidateQueries({ queryKey: keys.me });
    }
  }, [maintenanceKey, qc]);

  React.useEffect(
    () =>
      onBackendUnavailable(() => {
        const state = qc.getQueryState(keys.version);
        // Already checking, or already known to be away (fast poll on).
        if (!state || state.fetchStatus !== "idle" || state.status === "error") return;
        void qc.refetchQueries({ queryKey: keys.version, exact: true });
      }),
    [qc],
  );

  return status;
}
